import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showErrorToast } from "../toastUtils";
import { OAuthCallback } from "../api/tool";
import { CircularProgress } from "@mui/material";

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const getErrorMessage = useCallback((error) => {
    // Handle different error types and structures
    if (typeof error === 'string') {
      return error;
    }
    
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    if (error?.data?.message) {
      return error.data.message;
    }
    
    return "An unexpected error occurred during OAuth authentication";
  }, []);

  const handleOAuthError = useCallback((error, context = "") => {
    console.error(`OAuth ${context} error:`, error);
    const errorMessage = getErrorMessage(error);
    
    // Show user-friendly error message
    showErrorToast(`OAuth Authentication Failed: ${errorMessage}`);
    
    // Navigate back to previous page or dashboard
    const storedState = JSON.parse(
      sessionStorage.getItem("postOAuthModalState") || "{}"
    );
    
    if (storedState?.returnTo) {
      navigate(storedState.returnTo, {
        state: { oauthError: errorMessage }
      });
    } else {
      navigate("/dashboard");
    }
  }, [navigate, getErrorMessage]);

  useEffect(() => {
    const processOAuthCallback = async () => {
      setLoading(true);
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const state = url.searchParams.get("state");
        const error = url.searchParams.get("error");
        const errorDescription = url.searchParams.get("error_description");

        // Check for OAuth provider errors first
        if (error) {
          const errorMsg = errorDescription || error;
          const fullErrorMsg = `OAuth provider error: ${errorMsg}`;
          handleOAuthError(fullErrorMsg, "provider");
          return;
        }

        if (!code || !state) {
          handleOAuthError("Missing authorization code or state parameter from OAuth provider", "parameters");
          return;
        }

        // Extract data from localStorage
        const userId = localStorage.getItem("mcp_user_id");
        const oauthRes = localStorage.getItem("oauthResponse");

        if (!userId) {
          handleOAuthError("User session expired. Please log in again.", "session");
          return;
        }

        if (!oauthRes) {
          handleOAuthError("OAuth configuration missing. Please try connecting again.", "configuration");
          return;
        }

        let oauth;
        try {
          oauth = JSON.parse(oauthRes);
        } catch (parseError) {
          handleOAuthError("Invalid OAuth configuration data. Please try connecting again.", "configuration");
          return;
        }

        if (!oauth?.data?.state_data) {
          handleOAuthError("OAuth state data missing. Please try connecting again.", "state");
          return;
        }

        // Prepare the payload
        const payload = {
          code,
          state,
          state_data: oauth.data.state_data,
        };
        console.log("OAuth callback payload:", payload);

        // Read stored modal state
        const storedState = JSON.parse(
          sessionStorage.getItem("postOAuthModalState") || "{}"
        );

        if (!storedState?.tool?.toolName) {
          handleOAuthError("Provider information missing. Please try connecting again.", "provider");
          return;
        }

        // Use the refactored API function with proper provider mapping
        const providerName = storedState.tool.toolName.toLowerCase();
        console.log(`Processing OAuth callback for provider: ${providerName}`);
        
        const data = await OAuthCallback(
          providerName,
          payload,
          userId
        );

        console.log("OAuth callback response:", data);

        // Check for successful response
        if (data.status_code === 200 || data.success) {
          sessionStorage.setItem("toolAuth", String(true));
          setLoading(false);
          
          if (storedState?.returnTo) {
            navigate(storedState.returnTo, {
              state: { reopenFinalConnectModal: true },
            });
          } else {
            navigate("/dashboard");
          }
        } else {
          // Handle backend errors
          const errorMsg = data.message || data.error || "Authentication failed";
          handleOAuthError(errorMsg, "backend");
        }

      } catch (err) {
        handleOAuthError(err, "callback");
        // Only clean up session storage on error
        sessionStorage.removeItem("postOAuthModalState");
      } finally {
        setLoading(false);
        localStorage.removeItem("oauthResponse");
      }
    };
    processOAuthCallback();
  }, [navigate, handleOAuthError]);

  return loading && <CircularProgress />;
}
